import queryString from "query-string";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { GridSortDirection, GridSortItem } from "@mui/x-data-grid";

import { ICompanyFilters, IParsedFilters } from "@interfaces/filter.type";

import { useCompanyStore } from "@store/companyStore";

//TODO: Initial state should be based on local storage
export const FILTERS_INITIAL_STATE = {
  pageSize: 50,
  sort: [{ field: "numEmployees", sort: "desc" }],
} as ICompanyFilters;

export const EMBEDDING_SEARCH_INITIAL_STATE = {
  pageSize: 50,
  sort: [{ field: "similarityScore", sort: "desc" }],
} as ICompanyFilters;

export const TECHNOLOGY_INITIAL_STATE = {
  pageSize: 20,
  sort: [{ field: "similarityScore", sort: "desc" }],
} as ICompanyFilters;

const defaultFiltersPerRoute: Record<string, ICompanyFilters> = {
  directory: {
    pageSize: 50,
    sort: [{ field: "numEmployees", sort: "desc" }],
  },
  technology: {
    pageSize: 20,
    sort: [{ field: "similarityScore", sort: "desc" }],
  },
};

function isISort(value: any): value is GridSortItem {
  return value && typeof value.field === "string" && typeof value.sort === "string";
}

export default function useFilters(initialFilters?: ICompanyFilters) {
  //TODO: persist filters per page in local storage
  const [filtersURL, setFiltersURL] = useState("");

  const [filtersObject, setFiltersObject] = useState<ICompanyFilters>(initialFilters ?? FILTERS_INITIAL_STATE);
  const location = useLocation();
  const [urlSearchParameters] = useSearchParams();
  const navigate = useNavigate();
  const { actions: CompanyActions } = useCompanyStore();

  const handleSetFilters = (newFilters: Partial<ICompanyFilters>) => {
    let filters = createObjectFromSearchParameters(urlSearchParameters);

    if (Object.keys(filters).length < 2) {
      const locationPathnameSplit = location.pathname.split("/");
      let currentLocation = locationPathnameSplit[1];
      if (location.pathname.startsWith("/portfolio/technology")) {
        currentLocation = locationPathnameSplit[2];
      }

      filters = defaultFiltersPerRoute[currentLocation as keyof typeof defaultFiltersPerRoute] ?? FILTERS_INITIAL_STATE;
    }

    const preppedValues = Object.entries({ ...filters, ...newFilters }).reduce((accumulator, [key, value]) => {
      if (!value && key !== "forProfit" && key !== "nonProfit") return accumulator;

      if (key === "sort" && Array.isArray(value) && value.length > 0 && isISort(value[0])) {
        accumulator["sort"] = [value[0]];
        return accumulator;
      }
      if (Array.isArray(value) && value.length === 0) return accumulator;
      accumulator = { ...accumulator, [key]: value };

      return accumulator;
    }, {} as ICompanyFilters);

    const search = createURLSearchParams(preppedValues);

    setFiltersURL(search);

    navigate(`${location.pathname}?${search}`, {
      replace: true,
    });
  };

  const handleResetFilters = () => {
    const { page, pageSize, search, sort } = filtersObject;

    const newURL = createURLSearchParams({
      page,
      pageSize,
      search,
      sort,
    });

    setFiltersObject({
      page,
      pageSize,
      search,
      sort,
    });

    CompanyActions.setCompanySelection([]);

    setFiltersURL(newURL);
    navigate(`${location.pathname}?${newURL}`, {
      replace: true,
    });
  };

  useEffect(() => {
    if (location.pathname.includes("smart-search")) {
      localStorage.setItem("filters", JSON.stringify(filtersObject));
    }
  }, [JSON.stringify(filtersObject), location.pathname]);

  useEffect(() => {
    if (urlSearchParameters.size > 0) {
      setFiltersObject(createObjectFromSearchParameters(urlSearchParameters));
    }
  }, [urlSearchParameters]);

  return { filtersObject, filtersURL, handleResetFilters, handleSetFilters };
}

export function createURLSearchParams(parameters: ICompanyFilters) {
  // Pre-process the params object to convert the `sort` array into a format that `queryString.stringify` can handle
  const preppedParameters = { ...parameters } as any;
  if (Array.isArray(preppedParameters.sort)) {
    preppedParameters.sort.forEach((value: any, index: number) => {
      if (!isISort(value)) return;
      preppedParameters[`sort[${index}][field]`] = value.field;
      preppedParameters[`sort[${index}][sort]`] = value.sort;
    });
    delete preppedParameters.sort;
  }

  // Convert the preppedParams object into a URL query string
  return queryString.stringify(preppedParameters, { arrayFormat: "bracket" });
}

/**
 * Converts an array of strings into a query string with the specified key.
 * @param key - The key to use for the query string.
 * @param values - The array of strings to convert.
 * @returns A query string with the specified key and values.
 * @example
 * convertStringArrayToQueryString("companyIds", ['123','456']); // Returns "companyIds[]=123&companyIds[]=456"
 */
export function convertArrayToQueryString(key: string, values: string[]) {
  return values.map((id) => `${key}[]=${id}`).join("&");
}

export function createObjectFromSearchParameters(searchParameters: URLSearchParams) {
  const parsed = queryString.parse(searchParameters.toString(), {
    arrayFormat: "bracket",
    parseBooleans: true,
    parseNumbers: true,
  }) as IParsedFilters;

  parsed.sort = [];
  for (
    let index = 0;
    parsed[`sort[${index}][field]` as keyof ICompanyFilters] && parsed[`sort[${index}][sort]` as keyof ICompanyFilters];
    index++
  ) {
    parsed.sort.push({
      field: parsed[`sort[${index}][field]`] as string,
      sort: parsed[`sort[${index}][sort]`] as GridSortDirection,
    });
    delete parsed[`sort[${index}][field]` as keyof ICompanyFilters];
    delete parsed[`sort[${index}][sort]` as keyof ICompanyFilters];
  }

  return parsed as ICompanyFilters;
}
